import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { fetchEvents, fetchShopItems } from '../api/public'
import { CmsImage } from '../components/CmsImage'
import { EventCard } from '../components/EventCard'
import { HomeHero } from '../components/HomeHero'
import { HomeRecap } from '../components/HomeRecap'
import { usePageMeta } from '../hooks/usePageMeta'
import { schemaEventList, schemaGraph, schemaShopList, schemaWebPage } from '../lib/schema-org'

export function HomePage() {
  const eventsQ = useQuery({
    queryKey: ['events', 'upcoming'],
    queryFn: () => fetchEvents({ status: 'upcoming' }),
  })

  const shopQ = useQuery({
    queryKey: ['shop-items'],
    queryFn: fetchShopItems,
    staleTime: 5 * 60 * 1000,
  })

  // Past event pinned in the CMS — surfaces its gallery without a detour via /events.
  const recapQ = useQuery({
    queryKey: ['events', 'featured-home'],
    queryFn: () => fetchEvents({ status: 'featured-home' }),
    staleTime: 5 * 60 * 1000,
  })

  const recap = useMemo(() => {
    const ev = recapQ.data?.events?.[0]
    return ev && ev.displayStatus === 'past' ? ev : null
  }, [recapQ.data?.events])

  const events = useMemo(
    () =>
      [...(eventsQ.data?.events ?? [])].sort(
        (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
      ),
    [eventsQ.data?.events]
  )

  const shopItems = shopQ.data?.items ?? []

  const jsonLd = useMemo(
    () =>
      schemaGraph(
        schemaWebPage({
          path: '/',
          name: 'BNKR — Bunker Events, Label & Verein',
          description:
            'Events, Releases und Artists von BNKR — Bunker Events und Musik-Label aus der Ostschweiz.',
          pageType: 'WebPage',
        }),
        schemaEventList(events.slice(0, 12)),
        shopItems.length ? schemaShopList(shopItems) : { '@type': 'ItemList', name: 'BNKR Shop', numberOfItems: 0 },
      ),
    [events, shopItems],
  )

  usePageMeta({
    title: 'Home',
    description: 'BNKR — Bunker Events, Label und Verein. Demnächst Events, Releases und Artists.',
    canonical: '/',
    jsonLd,
  })

  return (
    <>
      <HomeHero events={events} loading={eventsQ.isLoading} />

      {recap && <HomeRecap ev={recap} />}

      <section className="section" id="events">
        <div className="wrap">
          <div className="section-title">
            <div>
              <div className="kicker crt">↳ /events</div>
              <h2 className="display h2" style={{ marginTop: 14, fontSize: 'clamp(22px, 7vw, 36px)', overflowWrap: 'break-word' }}>
                Kommende
                <br />
                Veranstaltungen
              </h2>
            </div>
          </div>

          {eventsQ.isError && <p className="error">Events konnten nicht geladen werden.</p>}
          {eventsQ.data && events.length === 0 && !eventsQ.isLoading && (
            <p className="muted">Zurzeit keine kommenden Termine.</p>
          )}
          <div className="ev-list">
            {events.map((ev) => (
              <EventCard key={ev.id} ev={ev} />
            ))}
          </div>

          <p style={{ marginTop: 32 }}>
            <Link className="btn btn-crt" to="/events">
              Alle Events →
            </Link>
          </p>
        </div>
      </section>

      {shopItems.length > 0 && (
        <section className="section" id="shop">
          <div className="wrap">
            <div className="sect-head">
              <div>
                <div className="kicker crt">↳ /shop</div>
                <h2 className="display h2" style={{ marginTop: 14 }}>Shop</h2>
              </div>
              <a className="btn btn-crt" href="https://foursix.ch/collections/bnkr" target="_blank" rel="noreferrer">
                Alle Artikel →
              </a>
            </div>

            <div className="shop-grid">
              {shopItems.map((item) => (
                <a
                  key={item.id}
                  className="product"
                  href={item.linkUrl ?? 'https://foursix.ch/collections/bnkr'}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                >
                  <div className="pic">
                    {item.imageUrl ? (
                      <CmsImage
                        src={item.imageUrl}
                        size="thumb"
                        alt={item.title}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="ph" style={{ position: 'absolute', inset: 0 }} />
                    )}
                  </div>
                  <div className="nm">{item.title}</div>
                  {item.subtitle && <div className="pr">{item.subtitle}</div>}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}
